import { parseClassificationSearch } from "@/features/change-requests/classification-filtering";
import { restoreCrBoardView } from "@/features/change-requests/cr-board-view-memory";
import { parseCrSortSearch } from "@/features/change-requests/cr-sorting";
import { parsePrioritySearch } from "@/features/change-requests/priority-filtering";
import { ProjectChangeRequestsPage } from "@/features/projects/project-change-requests.page";
import { isProjectFeatureEnabled } from "@/features/projects/utils/project-features";
import { projectHomeRedirect } from "@/features/projects/utils/project-home-destination";
import { IncognitoChangeRequestsPage } from "@/features/superuser/incognito/project/incognito-change-requests.page";
import { useWorkspaceAccess } from "@/features/workspaces/use-workspace-access";
import { ProjectRoute } from "@/routes/_authenticated/workspaces/$slug/projects/$projectSlug";
import { createFileRoute, redirect } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/_authenticated/workspaces/$slug/projects/$projectSlug/change-requests",
)({
  validateSearch: (search) => ({
    priority: parsePrioritySearch(search.priority),
    // CR-118: the CR type filter, URL-backed exactly like priority.
    type: parseClassificationSearch(search.type),
    // CR-140: the sort, `<field>-<direction>`; the default is never written.
    sort: parseCrSortSearch(search.sort),
  }),
  beforeLoad: ({ context, params, search, cause }) => {
    if (!isProjectFeatureEnabled(context.project, "show_change_requests")) {
      // CR-144: straight to the project's resolved home, not the bare root.
      throw projectHomeRedirect(context.project, params, context.workspaceAccess);
    }
    // CR-140: a bare entry (sidebar link from another page, typed URL,
    // bookmark) re-applies the view remembered for this tab. Enter-only:
    // `stay` re-runs on every search change here and would undo a clear (so a
    // bare link clicked while already on the board resets it), and a preload
    // must not redirect. `replace` keeps the bare URL out of the history; the
    // updater keeps any ancestor search keys. CR-165: incognito reads no
    // per-user view memory.
    if (context.workspaceAccess.mode !== "member") {
      return;
    }
    const restored = restoreCrBoardView({
      cause,
      search,
      scope: { userId: context.user.id, projectId: context.project.id },
    });
    if (restored) {
      throw redirect({
        to: "/workspaces/$slug/projects/$projectSlug/change-requests",
        params: { slug: params.slug, projectSlug: params.projectSlug },
        search: (prev) => ({ ...prev, ...restored }),
        replace: true,
      });
    }
  },
  component: ProjectChangeRequestsPageRoute,
});

// CR-165: incognito mode gets the display-only board (no drag, no archive, no
// New CR); the member page is untouched.
function ProjectChangeRequestsPageRoute() {
  const workspaceAccess = useWorkspaceAccess();
  const { project } = ProjectRoute.useRouteContext();

  if (workspaceAccess.mode === "incognito") {
    return <IncognitoChangeRequestsPage project={project} />;
  }

  return <ProjectChangeRequestsPage />;
}
